import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Invoice, InvoicePayment, PaginatedInvoices } from "@/types/invoice";
import type { InvoiceFormData } from "@/validators/invoice";

export interface ListInvoiceParams {
  search?: string;
  status?: string;
  companyId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  perPage?: number;
}

export interface CreatePaymentData {
  amount: number;
  paymentDate: string;
  method: string;
  notes?: string;
  accountId?: string;
}

const KEYS = {
  list: (params: ListInvoiceParams) => ["invoices", params] as const,
  detail: (id: string) => ["invoices", id] as const,
  payments: (id: string) => ["invoices", id, "payments"] as const,
};

export function useInvoices(params: ListInvoiceParams = {}) {
  return useQuery({
    queryKey: KEYS.list(params),
    queryFn: () =>
      api.get<PaginatedInvoices>("/invoices", { params }).then((r) => r.data),
  });
}

export function useInvoice(id: string) {
  return useQuery({
    queryKey: KEYS.detail(id),
    queryFn: () => api.get<Invoice>(`/invoices/${id}`).then((r) => r.data),
    enabled: !!id,
  });
}

export function useCreateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: InvoiceFormData) =>
      api.post<Invoice>("/invoices", data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["invoices"] }),
  });
}

export function useUpdateInvoice(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: InvoiceFormData) =>
      api.put<Invoice>(`/invoices/${id}`, data).then((r) => r.data),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.setQueryData(KEYS.detail(id), updated);
    },
  });
}

export function useDeleteInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/invoices/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["invoices"] }),
  });
}

export function useCreateInvoicePayment(invoiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreatePaymentData) =>
      api.post<InvoicePayment>(`/invoices/${invoiceId}/payments`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: KEYS.detail(invoiceId) });
      // O pagamento gera um lançamento no fluxo de caixa
      qc.invalidateQueries({ queryKey: ["cash-transactions"] });
      qc.invalidateQueries({ queryKey: ["cash-accounts"] });
      qc.invalidateQueries({ queryKey: ["cash-flow-summary"] });
    },
  });
}
